/**
 * ADR-0207 §3.4 — when the workspace's AI route can't serve a request, the hub
 * answers with one of four codes and its own teaching text. Panels show that
 * text verbatim (the console never rewords it); tenant admins also get a link
 * to the AI provider connection, or to create one.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch, ApiRequestError } from "./api.js";
import { AI_PROVIDER_NEW_PATH, aiProviderIntegrationPath, type AiProviderStatus } from "./aiProvider.js";

export const AI_ROUTE_ERROR_CODES: ReadonlySet<string> = new Set([
  "ai_provider_not_configured",
  "ai_provider_unavailable",
  "ai_model_not_served",
  "ai_capability_not_served"
]);

/** The route code carried by a hub error, or null for anything else. */
export function aiRouteErrorCode(e: unknown): string | null {
  return e instanceof ApiRequestError && AI_ROUTE_ERROR_CODES.has(e.code) ? e.code : null;
}

/** The admin's way to the fix. Renders nothing for non-admins or non-route errors. */
export function AiRouteErrorLink(props: { code: string | null | undefined }): JSX.Element | null {
  const [status, setStatus] = useState<AiProviderStatus | null>(null);
  const active = Boolean(props.code && AI_ROUTE_ERROR_CODES.has(props.code));

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void (async () => {
      try {
        const s = await apiFetch<AiProviderStatus>("/api/tenant/ai-provider/status");
        if (!cancelled) setStatus(s);
      } catch {
        if (!cancelled) setStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  if (!active || !status?.can_manage) return null;
  return status.integration_id ? (
    <Link to={aiProviderIntegrationPath(status.integration_id)}>Open the AI provider connection</Link>
  ) : (
    <Link to={AI_PROVIDER_NEW_PATH}>Connect an AI provider</Link>
  );
}
